import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { VEHICLE_SOURCE_NEEDS_REVIEW_FILTER } from "../../../../constants/vehicleSourceConstants";
import {
  createVehicleSource,
  fetchVehicleSources,
  setVehicleSourceActive,
  updateVehicleSource,
} from "../../../../services/vehicleSourceApi";
import { runVehicleSourceDiscovery } from "../../../../services/vehicleDiscoveryApi";
import { runVehicleSourceDetailFetch } from "../../../../services/vehicleListingApi";
import {
  canRunVehicleDealerDetailFetchUi,
  canRunVehicleDealerDiscoveryUi,
  canRunVehicleDetailFetchUi,
  canRunVehicleDiscoveryUi,
} from "../../../../constants/vehicleDiscoveryConstants";

export function useVehicleSourcesAdmin({ defaultSourceStatus = "" } = {}) {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [search, setSearch] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [sourceStatus, setSourceStatus] = useState(defaultSourceStatus);
  const needsReviewFilter = sourceStatus === VEHICLE_SOURCE_NEEDS_REVIEW_FILTER;
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [allowedFilter, setAllowedFilter] = useState("");
  const [robotsReview, setRobotsReview] = useState("");
  const [termsReview, setTermsReview] = useState("");
  const [accessReview, setAccessReview] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [sort, setSort] = useState("priority");
  const [sortDir, setSortDir] = useState("asc");

  const [modalMode, setModalMode] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [discoveryBusyId, setDiscoveryBusyId] = useState("");
  const [detailBusyId, setDetailBusyId] = useState("");

  const loadSources = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleSources({
        search: search.trim() || undefined,
        sourceType: sourceType || undefined,
        sourceStatus: needsReviewFilter ? undefined : sourceStatus || undefined,
        needsReview: needsReviewFilter || undefined,
        isActive: isActiveFilter === "" ? undefined : isActiveFilter === "true",
        allowedToIngest: allowedFilter === "" ? undefined : allowedFilter === "true",
        robotsReviewStatus: robotsReview || undefined,
        termsReviewStatus: termsReview || undefined,
        accessReviewStatus: accessReview || undefined,
        city: city.trim() || undefined,
        department: department.trim() || undefined,
        sort,
        sortDir,
      });
      setSources(data.sources || []);
      setSummary(data.summary);
    } catch (error) {
      setSources([]);
      setSummary(null);
      setErrorMessage(error.message || "Unable to load vehicle sources.");
    } finally {
      setLoading(false);
    }
  }, [
    search,
    sourceType,
    sourceStatus,
    needsReviewFilter,
    isActiveFilter,
    allowedFilter,
    robotsReview,
    termsReview,
    accessReview,
    city,
    department,
    sort,
    sortDir,
  ]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const replaceSource = (updated, ingestReadiness) => {
    setSources((current) =>
      current.map((row) =>
        row.id === updated.id ? { ...updated, ingestReadiness } : row
      )
    );
  };

  const openModal = (mode, source = null) => {
    setModalMode(mode);
    setSelectedSource(source);
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedSource(null);
  };

  const handleSave = async (payload) => {
    if (modalMode === "create") {
      const created = await createVehicleSource(payload);
      setSources((current) => [...current, created].sort((a, b) => a.priority - b.priority));
      setToast(`Created ${created.sourceName}`);
      return;
    }

    if (!selectedSource?.id) return;
    const { source: updated, ingestReadiness } = await updateVehicleSource(
      selectedSource.id,
      payload
    );
    replaceSource(updated, ingestReadiness);
    setToast(`Updated ${updated.sourceName}`);
  };

  const handleReviewSaved = (updated, ingestReadiness) => {
    replaceSource(updated, ingestReadiness);
    setToast(`Review saved for ${updated.sourceName}`);
    loadSources();
  };

  const handleToggleActive = async (source) => {
    if (!canManage) return;
    try {
      const { source: updated, ingestReadiness } = await setVehicleSourceActive(
        source.id,
        !source.isActive
      );
      replaceSource(updated, ingestReadiness);
      setToast(
        updated.isActive ? `${updated.sourceName} activated` : `${updated.sourceName} deactivated`
      );
    } catch (error) {
      setToast(error.message || "Unable to update active flag.");
    }
  };

  const handleRunDetailFetch = async (source, { dryRun = false, retryFailed = false } = {}) => {
    if (!canManage) return;
    const isMarketplace = source.sourceType === "MARKETPLACE";
    const isDealer = source.sourceType === "DEALER";
    const eligibility = isMarketplace
      ? canRunVehicleDetailFetchUi(source)
      : isDealer
        ? canRunVehicleDealerDetailFetchUi(source)
        : { allowed: false, reasons: ["Unsupported source type."] };
    if (!dryRun && !eligibility.allowed) {
      setToast(eligibility.reasons[0] || "Source is not eligible for detail fetch.");
      return;
    }
    setDetailBusyId(source.id);
    setToast("");
    try {
      const result = await runVehicleSourceDetailFetch(source.id, {
        dryRun,
        limit: 25,
        retryFailed,
      });
      setToast(
        `${dryRun ? "Dry-run detail" : "Detail fetch"} ${result.runStatus}: ${result.recordsParsed ?? 0} parsed (${result.recordsInserted ?? 0} new listings)`
      );
    } catch (error) {
      const detail =
        error.reasons?.length > 0 ? error.reasons.join("; ") : error.message || "Detail fetch failed.";
      setToast(detail);
    } finally {
      setDetailBusyId("");
    }
  };

  const handleRunDiscovery = async (source, { dryRun = false } = {}) => {
    if (!canManage) return;
    const isMarketplace = source.sourceType === "MARKETPLACE";
    const isDealer = source.sourceType === "DEALER";
    const eligibility = isMarketplace
      ? canRunVehicleDiscoveryUi(source)
      : isDealer
        ? canRunVehicleDealerDiscoveryUi(source)
        : { allowed: false, reasons: ["Unsupported source type."] };
    if (!dryRun && !eligibility.allowed) {
      setToast(eligibility.reasons[0] || "Source is not eligible for discovery.");
      return;
    }
    setDiscoveryBusyId(source.id);
    setToast("");
    try {
      const result = await runVehicleSourceDiscovery(source.id, { dryRun, limit: 50 });
      setToast(
        `${dryRun ? "Dry-run" : "Discovery"} ${result.runStatus}: ${result.recordsDiscovered ?? 0} URLs (${result.recordsInserted ?? 0} new)`
      );
    } catch (error) {
      const detail =
        error.reasons?.length > 0 ? error.reasons.join("; ") : error.message || "Discovery failed.";
      setToast(detail);
    } finally {
      setDiscoveryBusyId("");
    }
  };

  return {
    canManage,
    sources,
    summary,
    loading,
    errorMessage,
    toast,
    search,
    setSearch,
    sourceType,
    setSourceType,
    sourceStatus,
    setSourceStatus,
    needsReviewFilter,
    isActiveFilter,
    setIsActiveFilter,
    allowedFilter,
    setAllowedFilter,
    robotsReview,
    setRobotsReview,
    termsReview,
    setTermsReview,
    accessReview,
    setAccessReview,
    city,
    setCity,
    department,
    setDepartment,
    sort,
    setSort,
    sortDir,
    setSortDir,
    modalMode,
    selectedSource,
    discoveryBusyId,
    detailBusyId,
    loadSources,
    openModal,
    closeModal,
    handleSave,
    handleReviewSaved,
    handleToggleActive,
    handleRunDetailFetch,
    handleRunDiscovery,
  };
}
